// xpEarned is stored as plain decimal — no normalization needed.

// ============================================================================
// Type Definitions
// ============================================================================

/** Minimum required structure for an XP record */
export interface XpRecord {
  xpEarned: number
}

/** Minimum required structure for a weight record */
export interface WeightRecord {
  calculatedWeight: number
}

/** Minimum required structure for a lesson with weights */
export interface LessonWithWeights<W extends WeightRecord = WeightRecord> {
  id: string | number
  lessonWeights: W[]
}

/** Minimum required structure for a learn progress with earned XPs */
export interface LearnProgressWithXps<X extends XpRecord = XpRecord> {
  lessonId: string | number
  earnedXps: X[]
}

/** Individual grade result for a single lesson */
export interface IndividualGrade {
  grade: number
  totalXpEarned: number
}

/** Complete grade calculation result */
export interface GradeCalculationResult {
  /** Overall grade percentage (0-100) */
  grade: number
  /** Total XP earned across all lessons */
  totalXp: number
  /** Total XP that could be earned across all lessons */
  totalXpCouldBeEarned: number
  /** Individual grades for each lesson progress */
  individualGrades: IndividualGrade[]
}

// ============================================================================
// Core Calculation Functions
// ============================================================================

/**
 * Calculates the total XP earned from a list of XP records.
 *
 * @param xpRecords - Array of objects containing xpEarned property
 * @returns Normalized total XP earned
 *
 * @example
 * const total = calculateTotalXpEarned([{ xpEarned: 10 }, { xpEarned: 5 }])
 * // Returns: 15
 */
export function calculateTotalXpEarned<T extends XpRecord>(xpRecords: T[]): number {
  return xpRecords.reduce((sum, record) => sum + record.xpEarned, 0)
}

/**
 * Calculates the total XP that could be earned from a list of weight records.
 *
 * @param weightRecords - Array of objects containing calculatedWeight property
 * @returns Total possible XP
 *
 * @example
 * const maxXp = calculateTotalPossibleXp([{ calculatedWeight: 20 }, { calculatedWeight: 10 }])
 * // Returns: 30
 */
export function calculateTotalPossibleXp<T extends WeightRecord>(weightRecords: T[]): number {
  return weightRecords.reduce((sum, record) => sum + record.calculatedWeight, 0)
}

/**
 * Calculates a grade percentage from earned and possible XP.
 * Returns 0 if no XP could be earned to avoid division by zero.
 *
 * @param earnedXp - Total XP earned
 * @param possibleXp - Total XP that could be earned
 * @param round - Whether to round the result (default: false)
 * @returns Grade as a percentage (0-100)
 *
 * @example
 * const grade = calculateGrade(15, 20)
 * // Returns: 75
 */
export function calculateGrade(earnedXp: number, possibleXp: number, round = false): number {
  if (possibleXp === 0) return 0

  const grade = (earnedXp / possibleXp) * 100

  return round ? Math.round(grade) : grade
}

// ============================================================================
// High-Level Grade Calculation Functions
// ============================================================================

/**
 * Calculates an individual grade for a single lesson progress.
 *
 * @param learnProgress - Learn progress record with earned XPs
 * @param lesson - Lesson record with weights
 * @param options - Calculation options
 * @returns Individual grade result
 *
 * @example
 * const result = calculateIndividualLessonGrade(progress, lesson)
 * // Returns: { grade: 85, totalXpEarned: 17 }
 */
export function calculateIndividualLessonGrade<
  X extends XpRecord,
  W extends WeightRecord,
  P extends LearnProgressWithXps<X>,
  L extends LessonWithWeights<W>,
>(learnProgress: P, lesson: L, options: { round?: boolean } = {}): IndividualGrade {
  const { round = true } = options

  const totalXpEarned = calculateTotalXpEarned(learnProgress.earnedXps)
  const totalXpPossible = calculateTotalPossibleXp(lesson.lessonWeights)
  const grade = calculateGrade(totalXpEarned, totalXpPossible, round)

  return { grade, totalXpEarned }
}

/**
 * Calculates overall grade from pre-calculated totals.
 * Useful when you already have totalXp and totalXpCouldBeEarned.
 *
 * @param totalXp - Total XP earned
 * @param totalXpCouldBeEarned - Total XP that could be earned
 * @param options - Calculation options
 * @returns Grade percentage (0-100)
 *
 * @example
 * const grade = calculateOverallGrade(80, 100)
 * // Returns: 80
 */
export function calculateOverallGrade(
  totalXp: number,
  totalXpCouldBeEarned: number,
  options: { round?: boolean } = {},
): number {
  const { round = false } = options
  return calculateGrade(totalXp, totalXpCouldBeEarned, round)
}

/**
 * Calculates grades based on XP for all lessons and individual progress.
 * This is the most flexible version that accepts any data structure with required properties.
 *
 * @param learnProgresses - Array of learn progress records with earned XPs
 * @param lessons - Array of lesson records with weights
 * @param options - Calculation options
 * @returns Complete grade calculation result
 *
 * @example
 * // With full database types
 * const result = calculateGradeBasedOnXp({ learnProgresses, lessons })
 *
 * @example
 * // With minimal custom types
 * const result = calculateGradeBasedOnXp({
 *   learnProgresses: [{ lessonId: 1, earnedXps: [{ xpEarned: 1000 }] }],
 *   lessons: [{ id: 1, lessonWeights: [{ calculatedWeight: 20 }] }]
 * })
 */
export function calculateGradeBasedOnXp<
  X extends XpRecord = XpRecord,
  W extends WeightRecord = WeightRecord,
  P extends LearnProgressWithXps<X> = LearnProgressWithXps<X>,
  L extends LessonWithWeights<W> = LessonWithWeights<W>,
>(props: {
  learnProgresses: P[]
  lessons: L[]
  options?: {
    /** Whether to round individual grades (default: true) */
    roundIndividual?: boolean
    /** Whether to round overall grade (default: false) */
    roundOverall?: boolean
  }
}): GradeCalculationResult {
  const { learnProgresses, lessons, options = {} } = props
  const { roundIndividual = true, roundOverall = false } = options

  // Calculate total XP earned across all lessons
  const totalXp = learnProgresses.reduce((sum, progress) => sum + calculateTotalXpEarned(progress.earnedXps), 0)

  // Calculate total XP that could be earned across all lessons
  const totalXpCouldBeEarned = lessons.reduce((sum, lesson) => sum + calculateTotalPossibleXp(lesson.lessonWeights), 0)

  // Calculate overall grade
  const grade = calculateGrade(totalXp, totalXpCouldBeEarned, roundOverall)

  // Calculate individual grades for each lesson progress
  const individualGrades = learnProgresses.map((learnProgress) => {
    const lesson = lessons.find((lesson) => lesson.id === learnProgress.lessonId)

    if (!lesson) {
      throw new Error(`Lesson with id "${learnProgress.lessonId}" not found`)
    }

    return calculateIndividualLessonGrade(learnProgress, lesson, { round: roundIndividual })
  })

  return { grade, totalXp, totalXpCouldBeEarned, individualGrades }
}

export type CalculateGradeBasedOnXpResponse = GradeCalculationResult
